import React, { useState, useEffect } from 'react';
import { FileText, Plus, Search, Download, Eye, Trash2, Edit2, Folder, FolderPlus, ChevronDown, ChevronRight, GripVertical } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { documentsAPI } from '../services/api';
import { documentsFoldersMockData } from '../data/mockData'; // Import mockup data

const DocumentsPageWithFolders = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [documents, setDocuments] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isFolderDialogOpen, setIsFolderDialogOpen] = useState(false);
  const [editingDocument, setEditingDocument] = useState(null);
  const [editingFolder, setEditingFolder] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [expandedFolders, setExpandedFolders] = useState(new Set());
  const [draggedItem, setDraggedItem] = useState(null);
  const [draggedItemType, setDraggedItemType] = useState(null); // 'document' or 'folder'
  const [searchQuery, setSearchQuery] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    type: '',
    category: '',
    size: '',
    url: '',
    folder_id: ''
  });

  const [folderFormData, setFolderFormData] = useState({
    name: '',
    color: '#3b82f6',
    category: ''
  });

  const categories = [
    { id: 'personal', name: 'Personal', color: 'bg-black', textColor: 'text-white' },
    { id: 'investments', name: 'Investments', color: 'bg-white', textColor: 'text-cyan-500' },
    { id: 'other', name: 'Other', color: 'bg-black', textColor: 'text-white' }
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    await Promise.all([fetchDocuments(), fetchFolders()]);
  };

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      // USING MOCKUP DATA - Don't fetch from API
      console.log('📄 Using mockup documents data:', documentsFoldersMockData.documents.length);
      setDocuments(documentsFoldersMockData.documents);
      // const data = await documentsAPI.getAll();
      // setDocuments(data);
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFolders = async () => {
    try {
      // USING MOCKUP DATA - Don't fetch from API
      console.log('📁 Using mockup folders data:', documentsFoldersMockData.folders.length);
      setFolders(documentsFoldersMockData.folders);
      // const response = await fetch('/api/folders/');
      // const data = await response.json();
      // setFolders(data.folders || []);
    } catch (error) {
      console.error('Error fetching folders:', error);
    }
  };

  // Folder Management
  const handleCreateFolder = async (e) => {
    e.preventDefault();
    try {
      const folderCategory = folderFormData.category || (selectedCategory === 'all' ? categories[0].id : selectedCategory);
      const response = await fetch('/api/folders/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: folderFormData.name,
          color: folderFormData.color,
          category: folderCategory,
          order: folders.filter(f => f.category === folderCategory).length
        })
      });
      
      if (!response.ok) throw new Error('Failed to create folder');
      
      setIsFolderDialogOpen(false);
      setFolderFormData({ name: '', color: '#3b82f6', category: '' });
      fetchFolders();
      alert('✅ Folder created successfully!');
    } catch (error) {
      console.error('Error creating folder:', error);
      alert('Failed to create folder');
    }
  };

  const handleUpdateFolder = async (e) => {
    e.preventDefault();
    try {
      const updateData = {
        name: folderFormData.name,
        color: folderFormData.color
      };
      
      // Only include category if it's changed
      if (folderFormData.category) {
        updateData.category = folderFormData.category;
      }
      
      const response = await fetch(`/api/folders/${editingFolder.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData)
      });
      
      if (!response.ok) throw new Error('Failed to update folder');
      
      setIsFolderDialogOpen(false);
      setEditingFolder(null);
      setFolderFormData({ name: '', color: '#3b82f6', category: '' });
      fetchFolders();
      alert('✅ Folder updated successfully!');
    } catch (error) {
      console.error('Error updating folder:', error);
      alert('Failed to update folder');
    }
  };

  const handleDeleteFolder = async (folderId) => {
    if (!window.confirm('Delete this folder? Documents inside will be unfiled.')) return;
    
    try {
      const response = await fetch(`/api/folders/${folderId}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) throw new Error('Failed to delete folder');
      
      fetchData();
      alert('✅ Folder deleted successfully!');
    } catch (error) {
      console.error('Error deleting folder:', error);
      alert('Failed to delete folder');
    }
  };

  const toggleFolder = (folderId) => {
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(folderId)) {
      newExpanded.delete(folderId);
    } else {
      newExpanded.add(folderId);
    }
    setExpandedFolders(newExpanded);
  };

  // Drag and Drop
  const handleDragStart = (e, item, type) => {
    setDraggedItem(item);
    setDraggedItemType(type);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDropOnFolder = async (e, targetFolder) => {
    e.preventDefault();
    
    if (draggedItemType === 'document' && draggedItem) {
      // Move document to folder
      try {
        await documentsAPI.update(draggedItem.id, { folder_id: targetFolder.id });
        fetchDocuments();
        alert('✅ Document moved to folder!');
      } catch (error) {
        console.error('Error moving document:', error);
        alert('Failed to move document');
      }
    }
    
    setDraggedItem(null);
    setDraggedItemType(null);
  };

  const handleDropOnUnfiled = async (e) => {
    e.preventDefault();
    
    if (draggedItemType === 'document' && draggedItem && draggedItem.folder_id) {
      // Remove document from folder
      try {
        await documentsAPI.update(draggedItem.id, { folder_id: null });
        fetchDocuments();
        alert('✅ Document removed from folder!');
      } catch (error) {
        console.error('Error removing from folder:', error);
        alert('Failed to remove from folder');
      }
    }
    
    setDraggedItem(null);
    setDraggedItemType(null);
  };

  // Document Management (original functions)
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let finalFormData = { ...formData };
      
      if (uploadedFile) {
        console.log('Uploading file:', uploadedFile.name);
        const uploadFormData = new FormData();
        uploadFormData.append('file', uploadedFile);
        
        const uploadResponse = await fetch('/api/uploads/media', {
          method: 'POST',
          body: uploadFormData
        });
        
        if (!uploadResponse.ok) throw new Error('File upload failed');
        
        const uploadData = await uploadResponse.json();
        finalFormData.url = uploadData.url;
      }
      
      if (!finalFormData.url) {
        alert('Please upload a file or enter a URL');
        return;
      }
      
      await documentsAPI.create(finalFormData);
      setIsDialogOpen(false);
      setUploadedFile(null);
      setFormData({ name: '', type: '', category: '', size: '', url: '', folder_id: '' });
      fetchDocuments();
      alert('✅ Document added successfully!');
    } catch (error) {
      console.error('Error creating document:', error);
      alert(`Failed to add document: ${error.message}`);
    }
  };

  const handleEditDocument = (document) => {
    setEditingDocument(document);
    setFormData({
      name: document.name || '',
      type: document.type || '',
      category: document.category || '',
      size: document.size || '',
      url: document.url || '',
      folder_id: document.folder_id || ''
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdateDocument = async (e) => {
    e.preventDefault();
    try {
      let updateData = { ...formData };
      
      // If a new file is uploaded, upload it first and update URL
      if (uploadedFile) {
        console.log('Uploading new file for document update:', uploadedFile.name);
        const uploadFormData = new FormData();
        uploadFormData.append('file', uploadedFile);
        
        const uploadResponse = await fetch('/api/uploads/media', {
          method: 'POST',
          body: uploadFormData
        });
        
        if (!uploadResponse.ok) throw new Error('File upload failed');
        
        const uploadData = await uploadResponse.json();
        updateData.url = uploadData.url;
        console.log('✅ New file uploaded, URL updated');
      } else if (!updateData.url && editingDocument.url) {
        // If no new file uploaded, keep the original URL
        updateData.url = editingDocument.url;
      }
      
      await documentsAPI.update(editingDocument.id, updateData);
      setIsEditDialogOpen(false);
      setEditingDocument(null);
      setUploadedFile(null); // Clear uploaded file
      setFormData({ name: '', type: '', category: '', size: '', url: '', folder_id: '' });
      fetchDocuments();
      alert('✅ Document updated successfully!');
    } catch (error) {
      console.error('Error updating document:', error);
      alert(`Failed to update document: ${error.message}`);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this document?')) {
      try {
        await documentsAPI.delete(id);
        fetchDocuments();
        alert('✅ Document deleted successfully!');
      } catch (error) {
        console.error('Error deleting document:', error);
        alert('Failed to delete document');
      }
    }
  };

  // Filter logic
  const filteredDocuments = documents.filter(doc => {
    const matchesCategory = selectedCategory === 'all' || doc.category === selectedCategory;
    const matchesSearch = !searchQuery || 
      doc.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (doc.type && doc.type.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  const filteredFolders = folders.filter(folder => {
    const matchesCategory = selectedCategory === 'all' || folder.category === selectedCategory;
    const folderNameMatches = folder.name.toLowerCase().includes(searchQuery.toLowerCase());
    
    // If no search query, just check category
    if (!searchQuery) return matchesCategory;
    
    // Show folder if its name matches
    if (folderNameMatches) return matchesCategory;
    
    // Also show folder if it contains any matching documents
    const hasMatchingDocs = filteredDocuments.some(doc => doc.folder_id === folder.id);
    
    return matchesCategory && hasMatchingDocs;
  });

  const unfiledDocuments = filteredDocuments.filter(doc => !doc.folder_id);

  const getDocumentsInFolder = (folderId) => {
    return filteredDocuments.filter(doc => doc.folder_id === folderId);
  };

  const getCategoryColor = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    return category ? category.color : 'bg-black';
  };

  return (
    <div className="h-screen p-8 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-white mb-1">Documents</h1>
          <p className="text-white text-sm">All documents organized by category</p>
        </div>
        <button
          onClick={() => setIsDialogOpen(true)}
          className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors font-medium"
        >
          <Plus className="w-5 h-5" />
          Upload Document
        </button>
      </div>

      {/* Category Tabs */}
      <div className="bg-transparent rounded-xl p-1 mb-6 inline-flex gap-1">
        <button
          onClick={() => setSelectedCategory('all')}
          className={`px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap text-sm ${
            selectedCategory === 'all'
              ? 'bg-white text-black'
              : 'text-white hover:text-white'
          }`}
        >
          All Documents ({documents.length + folders.length})
        </button>
        {categories.map((category) => {
          const docCount = documents.filter(d => d.category === category.id).length;
          const folderCount = folders.filter(f => f.category === category.id).length;
          const totalCount = docCount + folderCount;
          
          return (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap text-sm ${
                selectedCategory === category.id
                  ? 'bg-white text-black'
                  : 'text-white hover:text-white'
              }`}
            >
              <span className={category.textColor}>●</span> {category.name} ({totalCount})
            </button>
          );
        })}
      </div>

      {/* Search Bar and New Folder Button */}
      <div className="flex gap-3 mb-6">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white w-5 h-5" />
          <input
            type="text"
            placeholder="Search documents and folders..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-transparent border border-transparent focus:border-blue-500 rounded-lg text-white placeholder-white focus:outline-none transition-colors"
          />
        </div>
        <button
          onClick={() => {
            setEditingFolder(null);
            setFolderFormData({ name: '', color: '#3b82f6', category: '' });
            setIsFolderDialogOpen(true);
          }}
          className="flex items-center gap-2 px-4 py-2.5 bg-transparent hover:bg-white/10 text-white rounded-lg transition-colors font-medium"
        >
          <FolderPlus className="w-5 h-5" />
          New Folder
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-white">Loading...</div>
      ) : (
        <div className="flex-1 overflow-y-auto space-y-4 pr-2">
          {/* Folders */}
          {filteredFolders.map((folder) => {
            const folderDocs = getDocumentsInFolder(folder.id);
            const isExpanded = expandedFolders.has(folder.id);
            
            return (
              <div
                key={folder.id}
                className="relative rounded-2xl border border-white/60 overflow-hidden hover:border-white transition-all bg-transparent"
                onDragOver={handleDragOver}
                onDrop={(e) => handleDropOnFolder(e, folder)}
              >
                {/* Removed gradient overlay for clean transparent look */}
                
                {/* Folder Header */}
                <div className="relative flex items-center gap-4 p-5 cursor-pointer" onClick={() => toggleFolder(folder.id)}>
                  {isExpanded ? (
                    <ChevronDown className="w-5 h-5 text-white" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-white" />
                  )}
                  
                  <div className="flex items-center gap-3 flex-1">
                    <div className="relative w-12 h-12 rounded-xl flex items-center justify-center overflow-hidden bg-transparent">
                      <Folder className="w-6 h-6 relative z-10 text-white" />
                    </div>
                    
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-white font-medium text-base">{folder.name}</h3>
                        {folder.category && (() => {
                          const category = categories.find(c => c.id === folder.category);
                          return category ? (
                            <span className="text-xs px-2.5 py-0.5 rounded-full font-medium bg-transparent border border-white/60 text-white">
                            >
                              {category.name}
                            </span>
                          ) : null;
                        })()}
                      </div>
                      <p className="text-xs text-white">{folderDocs.length} documents</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingFolder(folder);
                        setFolderFormData({ name: folder.name, color: folder.color, category: folder.category || '' });
                        setIsFolderDialogOpen(true);
                      }}
                      className="p-2 hover:bg-white/5 rounded-lg text-white hover:text-white transition-colors"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteFolder(folder.id);
                      }}
                      className="p-2 hover:bg-white/5 rounded-lg text-white hover:text-red-400 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Folder Contents */}
                {isExpanded && (
                  <div className="relative px-5 pb-5 space-y-3 border-t border-white/60">
                    {folderDocs.length === 0 ? (
                      <p className="text-white text-sm italic py-8 text-center">
                        Drag and drop documents here to organize them in this folder
                      </p>
                    ) : (
                      <div className="pt-3 space-y-3">
                        {folderDocs.map((doc) => (
                          <DocumentCard
                            key={doc.id}
                            doc={doc}
                            categories={categories}
                            onDragStart={handleDragStart}
                            onEdit={handleEditDocument}
                            onDelete={handleDelete}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {/* Unfiled Documents */}
          {unfiledDocuments.length > 0 && (
            <div onDragOver={handleDragOver} onDrop={handleDropOnUnfiled}>
              {filteredFolders.length > 0 && (
                <h3 className="text-white font-medium text-sm mb-3 flex items-center gap-2 px-1">
                  <FileText className="w-4 h-4" />
                  Unfiled Documents
                </h3>
              )}
              <div className="space-y-2">
                {unfiledDocuments.map((doc) => (
                  <DocumentCard
                    key={doc.id}
                    doc={doc}
                    categories={categories}
                    onDragStart={handleDragStart}
                    onEdit={handleEditDocument}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            </div>
          )}

          {filteredDocuments.length === 0 && (
            <div className="text-center py-16 bg-transparent rounded-xl border border-[#2a3752]">
              <FileText className="w-16 h-16 mx-auto mb-4 text-gray-600" />
              <p className="text-white text-lg mb-2">No documents yet</p>
              <p className="text-white text-sm">Click "Upload Document" to add your first file</p>
            </div>
          )}
        </div>
      )}

      {/* Add Document Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="bg-black border border-white/60 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold">Upload Document</DialogTitle>
            <DialogDescription className="text-white">
              Add document information to your library
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Document Name *</label>
              <input
                type="text"
                placeholder="e.g., Contract_Agreement.pdf"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full px-4 py-2.5 bg-transparent border border-white/60 rounded-lg text-white placeholder-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Type *</label>
              <input
                type="text"
                placeholder="e.g., Contract, Report, Presentation"
                value={formData.type}
                onChange={(e) => setFormData({...formData, type: e.target.value})}
                className="w-full px-4 py-2.5 bg-transparent border border-white/60 rounded-lg text-white placeholder-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Category *</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({...formData, category: e.target.value})}
                className="w-full px-4 py-2.5 bg-transparent border border-white/60 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Select category</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Size</label>
              <input
                type="text"
                placeholder="e.g., 2.4 MB"
                value={formData.size}
                onChange={(e) => setFormData({...formData, size: e.target.value})}
                className="w-full px-4 py-2.5 bg-transparent border border-white/60 rounded-lg text-white placeholder-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">URL</label>
              <input
                type="url"
                placeholder="https://"
                value={formData.url}
                onChange={(e) => setFormData({...formData, url: e.target.value})}
                className="w-full px-4 py-2.5 bg-transparent border border-white/60 rounded-lg text-white placeholder-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Or Upload File</label>
              <div className="flex items-center gap-3">
                <label className="flex-1 cursor-pointer">
                  <div className="flex items-center justify-center gap-2 px-4 py-2.5 bg-white hover:bg-cyan-600 text-white rounded-lg transition-colors">
                    <span className="text-sm font-medium">Choose File</span>
                  </div>
                  <input
                    type="file"
                    onChange={(e) => setUploadedFile(e.target.files[0])}
                    className="hidden"
                  />
                </label>
                <span className="text-sm text-white">
                  {uploadedFile ? uploadedFile.name : 'No file chosen'}
                </span>
              </div>
              <p className="text-xs text-white mt-2">Upload a document file or enter URL above</p>
            </div>
            
            <DialogFooter className="flex gap-3 mt-6">
              <Button 
                type="button" 
                onClick={() => setIsDialogOpen(false)}
                className="flex-1 bg-transparent hover:bg-white/5 text-white"
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                Add Document
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Document Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="bg-black border border-white/60 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold">Edit Document</DialogTitle>
            <DialogDescription className="text-white">
              Update document information in your library
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateDocument} className="space-y-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Document Name *</label>
              <input
                type="text"
                placeholder="e.g., Contract_Agreement.pdf"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full px-4 py-2.5 bg-transparent border border-white/60 rounded-lg text-white placeholder-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Type *</label>
              <input
                type="text"
                placeholder="e.g., Contract, Report, Presentation"
                value={formData.type}
                onChange={(e) => setFormData({...formData, type: e.target.value})}
                className="w-full px-4 py-2.5 bg-transparent border border-white/60 rounded-lg text-white placeholder-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Category *</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({...formData, category: e.target.value})}
                className="w-full px-4 py-2.5 bg-transparent border border-white/60 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Select category</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Size</label>
              <input
                type="text"
                placeholder="e.g., 2.4 MB"
                value={formData.size}
                onChange={(e) => setFormData({...formData, size: e.target.value})}
                className="w-full px-4 py-2.5 bg-transparent border border-white/60 rounded-lg text-white placeholder-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Replace Document File (Optional)</label>
              <div className="flex items-center gap-3">
                <label className="flex-1 cursor-pointer">
                  <div className="px-4 py-2.5 bg-transparent border border-white/60 rounded-lg text-white hover:bg-white/10 transition-colors flex items-center justify-center">
                    <span className="text-sm">Choose New File</span>
                  </div>
                  <input
                    type="file"
                    onChange={(e) => setUploadedFile(e.target.files[0])}
                    className="hidden"
                  />
                </label>
                <span className="text-sm text-white flex-1">
                  {uploadedFile ? uploadedFile.name : 'No new file selected'}
                </span>
              </div>
              <p className="text-xs text-white mt-2">Upload a new file to replace the existing document</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Current File URL (Read-only)</label>
              <input
                type="text"
                value={formData.url}
                readOnly
                className="w-full px-4 py-2.5 bg-black border border-white/60 rounded-lg text-white cursor-not-allowed text-xs"
                placeholder="Document file URL"
              />
            </div>
            
            <DialogFooter className="flex gap-3 mt-6">
              <Button 
                type="button" 
                onClick={() => {
                  setIsEditDialogOpen(false);
                  setUploadedFile(null); // Clear uploaded file on cancel
                }}
                className="flex-1 bg-transparent hover:bg-white/5 text-white"
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                Update Document
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Folder Dialog */}
      <Dialog open={isFolderDialogOpen} onOpenChange={setIsFolderDialogOpen}>
        <DialogContent className="bg-black border border-white/60 text-white">
          <DialogHeader>
            <DialogTitle>{editingFolder ? 'Edit Folder' : 'Create New Folder'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={editingFolder ? handleUpdateFolder : handleCreateFolder} className="space-y-4">
            <div>
              <label className="block text-sm text-white mb-2">Folder Name *</label>
              <input
                type="text"
                placeholder="Folder Name"
                value={folderFormData.name}
                onChange={(e) => setFolderFormData({...folderFormData, name: e.target.value})}
                className="w-full px-3 py-2 bg-white/5 border border-white/60 rounded-lg text-white"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm text-white mb-2">Category *</label>
              <select
                value={folderFormData.category || (selectedCategory === 'all' ? '' : selectedCategory)}
                onChange={(e) => setFolderFormData({...folderFormData, category: e.target.value})}
                className="w-full px-3 py-2 bg-white/5 border border-white/60 rounded-lg text-white"
                required
              >
                <option value="">Select category</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm text-white mb-2">Folder Color</label>
              <input
                type="color"
                value={folderFormData.color}
                onChange={(e) => setFolderFormData({...folderFormData, color: e.target.value})}
                className="w-full h-10 bg-white/5 border border-white/60 rounded-lg"
              />
            </div>
            <DialogFooter>
              <Button type="submit" className="bg-black hover:bg-white/10">
                {editingFolder ? 'Update Folder' : 'Create Folder'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Document Card Component
const DocumentCard = ({ doc, categories, onDragStart, onEdit, onDelete }) => {
  const category = categories?.find(c => c.id === doc.category);
  
  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, doc, 'document')}
      className="relative flex items-center gap-4 p-5 rounded-2xl border border-white/60 hover:border-white transition-all cursor-move group overflow-hidden bg-transparent"
    >
      {/* Clean transparent design - no gradient overlay */}
      
      {/* File Icon - simplified */}
      <div className="relative w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden bg-transparent">
        <FileText className="w-7 h-7 text-white relative z-10" />
      </div>
      
      {/* Document Info */}
      <div className="flex-1 min-w-0 relative z-10">
        <h4 className="text-white font-medium text-base mb-2 truncate">{doc.name}</h4>
        <div className="flex items-center gap-2 flex-wrap">
          {category && (
            <span className="text-xs px-2.5 py-1 rounded-full font-medium bg-transparent border border-white/60 text-white">
              {category.name}
            </span>
          )}
          {doc.type && (
            <span className="text-xs px-2.5 py-1 rounded-full font-medium bg-transparent border border-white/60 text-white">
              {doc.type}
            </span>
          )}
        </div>
        {(doc.size || doc.uploaded_at) && (
          <div className="flex items-center gap-3 mt-2">
            {doc.size && (
              <span className="text-xs text-white">Size: {doc.size}</span>
            )}
            {doc.uploaded_at && (
              <span className="text-xs text-white">
                Uploaded: {new Date(doc.uploaded_at).toLocaleDateString()}
              </span>
            )}
          </div>
        )}
      </div>
      
      {/* Actions */}
      <div className="flex items-center gap-1 relative z-10">
        {doc.url && (
          <>
            <a
              href={doc.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="p-2.5 hover:bg-white/5 rounded-lg text-white hover:text-white transition-colors"
              title="View"
            >
              <Eye className="w-4 h-4" />
            </a>
            <a
              href={doc.url}
              download
              onClick={(e) => e.stopPropagation()}
              className="p-2.5 hover:bg-white/5 rounded-lg text-white hover:text-white transition-colors"
              title="Download"
            >
              <Download className="w-4 h-4" />
            </a>
          </>
        )}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onEdit(doc);
          }}
          className="p-2.5 hover:bg-white/5 rounded-lg text-white hover:text-white transition-colors"
          title="Edit"
        >
          <Edit2 className="w-4 h-4" />
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete(doc.id);
          }}
          className="p-2.5 hover:bg-white/5 rounded-lg text-white hover:text-red-400 transition-colors"
          title="Delete"
        >
          <Trash2 className="w-4 h-4" />
        </button>
        <div className="p-2.5 text-white cursor-move">
          <GripVertical className="w-4 h-4" />
        </div>
      </div>
    </div>
  );
};

export default DocumentsPageWithFolders;
